import React, { useState } from 'react';
import UsageChart from './UsageChart';
import SessionControls from './SessionControls';
import { SessionUsageData, prepareChartData, combineChartData, formatSessionStartTime } from './utils';

interface SessionUsageProps {
  sessionUsage: SessionUsageData;
  onReset: () => void;
}

const SessionUsage: React.FC<SessionUsageProps> = ({
  sessionUsage,
  onReset
}) => {
  const [isAdvancedView, setIsAdvancedView] = useState(false);

  const usageData = {
    input_tokens: prepareChartData(sessionUsage, 'input_tokens'),
    output_tokens: prepareChartData(sessionUsage, 'output_tokens'),
    reasoning_tokens: prepareChartData(sessionUsage, 'reasoning_tokens'),
    cache_creation_input_tokens: prepareChartData(sessionUsage, 'cache_creation_input_tokens'),
    cache_read_input_tokens: prepareChartData(sessionUsage, 'cache_read_input_tokens'),
    total_tokens: prepareChartData(sessionUsage, 'total_tokens'),

    input_charges: prepareChartData(sessionUsage, 'input_charges'),
    output_charges: prepareChartData(sessionUsage, 'output_charges'),
    reasoning_charges: prepareChartData(sessionUsage, 'reasoning_charges'),
    cache_creation_input_charges: prepareChartData(sessionUsage, 'cache_creation_input_charges'),
    cache_read_input_charges: prepareChartData(sessionUsage, 'cache_read_input_charges'),
    total_charges: prepareChartData(sessionUsage, 'total_charges')
  };

  const basicViewData = {
    input_tokens: combineChartData([
      usageData.input_tokens,
      usageData.cache_creation_input_tokens,
      usageData.cache_read_input_tokens
    ]),
    output_tokens: combineChartData([
      usageData.output_tokens,
      usageData.reasoning_tokens
    ]),
    total_tokens: usageData.total_tokens,

    input_charges: combineChartData([
      usageData.input_charges,
      usageData.cache_creation_input_charges,
      usageData.cache_read_input_charges
    ]),
    output_charges: combineChartData([
      usageData.output_charges,
      usageData.reasoning_charges
    ]),
    total_charges: usageData.total_charges
  };

  const sessionStartTime = formatSessionStartTime(sessionUsage.sessionStart);

  return (
    <>
      <hr className="border-gray-300 dark:border-gray-600 my-6" />
      <div className="mt-6 text-black dark:text-white text-center">
        <h2 className="text-xl font-semibold mb-4">Session Usage</h2>
        <div className="inline-flex items-center gap-2 bg-neutral-200 dark:bg-neutral-700 rounded-full px-3 py-1 mb-4">
          <button
            onClick={() => setIsAdvancedView(false)}
            className={`text-xs font-medium px-2 py-1 rounded-full transition-colors min-w-[72px] ${
              !isAdvancedView
                ? 'bg-cyan-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Basic
          </button>
          <button
            onClick={() => setIsAdvancedView(true)}
            className={`text-xs font-medium px-2 py-1 rounded-full transition-colors min-w-[72px] ${
              isAdvancedView
                ? 'bg-cyan-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Advanced
          </button>
        </div>

        <SessionControls startTime={sessionStartTime} onReset={onReset} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {isAdvancedView ? (
            <>
              <UsageChart data={usageData.input_tokens} title="Input Tokens" />
              <UsageChart data={usageData.output_tokens} title="Output Tokens" />
              <UsageChart data={usageData.reasoning_tokens} title="Reasoning Tokens" />
              <UsageChart data={usageData.cache_creation_input_tokens} title="Cache Write Tokens" />
              <UsageChart data={usageData.cache_read_input_tokens} title="Cache Read Tokens" />
              <UsageChart data={usageData.total_tokens} title="Total Tokens" />

              <UsageChart data={usageData.input_charges} title="Input Charges" isCurrency />
              <UsageChart data={usageData.output_charges} title="Output Charges" isCurrency />
              <UsageChart data={usageData.reasoning_charges} title="Reasoning Charges" isCurrency />
              <UsageChart data={usageData.cache_creation_input_charges} title="Cache Write Charges" isCurrency />
              <UsageChart data={usageData.cache_read_input_charges} title="Cache Read Charges" isCurrency />
              <UsageChart data={usageData.total_charges} title="Total Charges" isCurrency />
            </>
          ) : (
            <>
              <UsageChart data={basicViewData.input_tokens} title="Input Tokens" />
              <UsageChart data={basicViewData.output_tokens} title="Output Tokens" />
              <UsageChart data={basicViewData.total_tokens} title="Total Tokens" />

              <UsageChart data={basicViewData.input_charges} title="Input Charges" isCurrency />
              <UsageChart data={basicViewData.output_charges} title="Output Charges" isCurrency />
              <UsageChart data={basicViewData.total_charges} title="Total Charges" isCurrency />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SessionUsage;